import React, { useMemo, useState } from 'react';
import { addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { UserCheck, UserX, MapPin, Mail, Calendar, Users, AlertTriangle } from 'lucide-react';
import Swal from 'sweetalert2';
import { db } from '../../firebase.js';
import { useAuth } from '../../context/AuthContext.js';
import { useAdminContext } from '../../context/AdminContext.js';
import '../../styles/AprobacionUsuarios.css';

const TABS = [
    { id: 'pending', label: 'Pendientes' },
    { id: 'approved', label: 'Aprobados' },
    { id: 'rejected', label: 'Rechazados' },
];

const toDate = (value) => {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
    const d = toDate(value);
    return d ? d.toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }) : 'Sin fecha';
};

/**
 * Aprobación de Usuarios (2026-10-23): desde esa fecha los registros nuevos quedan con
 * approvalStatus 'pending' (lo pone createUserProfile) y no entran al panel hasta que un admin
 * los apruebe acá. Los perfiles anteriores no tienen el campo y cuentan como aprobados, así que
 * no aparecen en ninguna pestaña.
 */
const AprobacionUsuarios = () => {
    const { currentUser } = useAuth();
    const { users } = useAdminContext() || { users: [] };
    const [activeTab, setActiveTab] = useState('pending');
    const [processingId, setProcessingId] = useState(null);

    const usersById = useMemo(() => new Map((users || []).map(u => [u.id, u])), [users]);

    const byStatus = useMemo(() => {
        const groups = { pending: [], approved: [], rejected: [] };
        (users || []).forEach(u => {
            if (groups[u.approvalStatus]) groups[u.approvalStatus].push(u);
        });
        // Pendientes: el más viejo primero (orden de llegada). Revisados: el último revisado primero.
        groups.pending.sort((a, b) => (toDate(a.createdAt)?.getTime() || 0) - (toDate(b.createdAt)?.getTime() || 0));
        ['approved', 'rejected'].forEach(k => groups[k].sort((a, b) =>
            (toDate(b.approvalReviewedAt)?.getTime() || 0) - (toDate(a.approvalReviewedAt)?.getTime() || 0)));
        return groups;
    }, [users]);

    // Cuántos pendientes trae cada referente (ayuda a detectar registros masivos).
    const pendingPerReferrer = useMemo(() => {
        const counts = new Map();
        byStatus.pending.forEach(u => { if (u.referrerId) counts.set(u.referrerId, (counts.get(u.referrerId) || 0) + 1); });
        return counts;
    }, [byStatus]);

    const review = async (user, newStatus) => {
        const name = user.fullName || user.username || user.userEmail;
        let note = '';
        if (newStatus === 'approved') {
            const res = await Swal.fire({
                title: '¿Aprobar usuario?',
                html: `<strong>${escapeHtml(name)}</strong> va a poder ingresar al panel.`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Aprobar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#16a34a',
            });
            if (!res.isConfirmed) return;
        } else {
            const res = await Swal.fire({
                title: '¿Rechazar usuario?',
                html: `<strong>${escapeHtml(name)}</strong> no va a poder ingresar al panel.`,
                input: 'textarea',
                inputLabel: 'Motivo (opcional, solo lo ven los admins)',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Rechazar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#dc2626',
            });
            if (!res.isConfirmed) return;
            note = (res.value || '').trim();
        }

        setProcessingId(user.id);
        try {
            const reviewer = { uid: currentUser?.uid || null, email: currentUser?.email || null };
            const profileRef = doc(db, 'users', user.id, 'profile', 'data');
            await setDoc(profileRef, {
                approvalStatus: newStatus,
                approvalReviewedAt: serverTimestamp(),
                approvalReviewedBy: reviewer,
                approvalNote: note || null,
            }, { merge: true });
            await addDoc(collection(profileRef, 'admin_audit'), {
                type: 'approval_update',
                adminId: reviewer.uid,
                adminEmail: reviewer.email,
                changes: { approvalStatus: newStatus, approvalNote: note || null },
                previous: { approvalStatus: user.approvalStatus || null },
                createdAt: serverTimestamp(),
            });
            Swal.fire({
                title: newStatus === 'approved' ? 'Usuario aprobado' : 'Usuario rechazado',
                icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000,
            });
        } catch (error) {
            console.error('Error al actualizar la aprobación del usuario:', error);
            Swal.fire('Error', 'No se pudo actualizar el usuario.', 'error');
        } finally {
            setProcessingId(null);
        }
    };

    const list = byStatus[activeTab];

    return (
        <div className="main-panel approval-panel">
            <h2 className="section-title">Aprobación de Usuarios</h2>
            <p className="approval-intro">
                Los usuarios nuevos quedan pendientes y no pueden entrar al panel hasta que los apruebes.
                Revisá de dónde son y quién los invitó antes de decidir.
            </p>

            <div className="approval-tabs" role="tablist">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`approval-tab${activeTab === tab.id ? ' is-active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label} <span className="approval-tab__count">{byStatus[tab.id].length}</span>
                    </button>
                ))}
            </div>

            {list.length === 0 ? (
                <div className="approval-empty">
                    {activeTab === 'pending' ? 'No hay usuarios pendientes de aprobación.' : 'Todavía no hay usuarios en esta lista.'}
                </div>
            ) : (
                <div className="approval-list">
                    {list.map(user => {
                        const referrer = user.referrerId ? usersById.get(user.referrerId) : null;
                        const referrerPending = referrer && referrer.approvalStatus && referrer.approvalStatus !== 'approved';
                        const siblingsPending = user.referrerId ? (pendingPerReferrer.get(user.referrerId) || 0) - 1 : 0;
                        return (
                            <div key={user.id} className="approval-card">
                                <div className="approval-card__main">
                                    <div className="approval-card__identity">
                                        <h3>{user.fullName || 'Sin nombre'}</h3>
                                        <span className="approval-card__username">@{user.username || 'sin-usuario'}</span>
                                    </div>
                                    <ul className="approval-card__facts">
                                        <li><MapPin size={14} /> <span>País:</span> <strong>{user.country || 'No indicado'}</strong></li>
                                        <li><Mail size={14} /> <span>Email:</span> <strong>{user.userEmail || 'Sin email'}</strong></li>
                                        <li><Calendar size={14} /> <span>Registro:</span> <strong>{formatDate(user.createdAt)}</strong></li>
                                    </ul>
                                    <div className="approval-card__referrer">
                                        <div className="approval-card__referrer-title"><Users size={14} /> Invitado por</div>
                                        {referrer ? (
                                            <>
                                                <strong>{referrer.fullName || referrer.username || 'Sin nombre'}</strong>
                                                <span>@{referrer.username || 'sin-usuario'} · {referrer.userEmail || 'sin email'} · {referrer.country || 'país no indicado'}</span>
                                                {referrerPending && (
                                                    <span className="approval-flag"><AlertTriangle size={13} /> El referente tampoco está aprobado</span>
                                                )}
                                                {siblingsPending > 0 && (
                                                    <span className="approval-flag approval-flag--info">
                                                        Este referente tiene {siblingsPending} {siblingsPending === 1 ? 'otro registro pendiente' : 'otros registros pendientes'}
                                                    </span>
                                                )}
                                            </>
                                        ) : user.referrerId ? (
                                            <span>Referente con ID {user.referrerId.substring(0, 8)}… (no se encontró su perfil)</span>
                                        ) : (
                                            <span className="approval-flag"><AlertTriangle size={13} /> Sin vinculador (código de referido inválido o no informado)</span>
                                        )}
                                    </div>
                                    {activeTab !== 'pending' && (
                                        <div className="approval-card__review">
                                            {activeTab === 'approved' ? 'Aprobado' : 'Rechazado'} el {formatDate(user.approvalReviewedAt)}
                                            {user.approvalReviewedBy?.email ? ` por ${user.approvalReviewedBy.email}` : ''}
                                            {user.approvalNote ? <div className="approval-card__note">Motivo: {user.approvalNote}</div> : null}
                                        </div>
                                    )}
                                </div>
                                <div className="approval-card__actions">
                                    {activeTab !== 'approved' && (
                                        <button type="button" className="approval-btn approval-btn--approve" disabled={processingId === user.id} onClick={() => review(user, 'approved')}>
                                            <UserCheck size={16} /> Aprobar
                                        </button>
                                    )}
                                    {activeTab !== 'rejected' && (
                                        <button type="button" className="approval-btn approval-btn--reject" disabled={processingId === user.id} onClick={() => review(user, 'rejected')}>
                                            <UserX size={16} /> Rechazar
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

function escapeHtml(value = '') {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value).replace(/[&<>"']/g, (char) => map[char]);
}

export default AprobacionUsuarios;
